// SPEC-426 M2 A.1 — done-text ↔ evidence consistency.
//
// Forensics (SPEC-426, doc:dale KV): done bubbles kept claiming「N 张占位图，已自动启动生成」
// after every generated image had already landed — a stale claim contradicting the visible
// canvas. Worse (修复轮 A.1): worker replies claimed「自检完成/已按视觉规范完成整稿」while the
// process log held ZERO screenshot/critique phases — a text self-check impersonating a visual
// review that never ran.
//
// buildDoneText aligns a done-message draft with the evidence:
//   - self-claim layer: sentences claiming 自检/视觉检查通过/已按视觉规范 survive ONLY when the
//     process log carries a real 'critique' phase; without that evidence they are stripped
//     whole. Legit「按审查意见修正」-style sentences carry no self-claim marker and stay.
//   - replica-claim layer (修复轮 B.1 双重禁字 done 层): sentences CLAIMING 还原/复刻/100%
//     fidelity survive ONLY with grounding evidence (an 'extract'/'diff' phase — the SPEC-402
//     pipeline actually ran). Narrowed on purpose: absolute-fidelity words (100%/百分之百/
//     一模一样) are claims by themselves; 还原/复刻/仿制 count only when co-occurring with a
//     completion verb — so progressive narration (「正在复刻布局」), the ask-for-reference reply
//     and the honest「无参考图，按通用规范设计」declaration all survive.
//   - placeholder layer: any image object still lacks src (or generation failed) → the
//     placeholder-generation claim stays verbatim; all images landed → the claim sentence is
//     stripped whole — never rewritten into an「已自动完成」-style contradiction sentence.
//   - completeness layer (SPEC-427 D, opts.noReference 限定): insistsNoReference 路径（复刻意图+
//     无附图+坚持无参考）的「完整搭建/完整原型/可交付/整稿完成」类完整性宣称仅在 process 携
//     verdict.pass=true 的 critique 证据时保留；「待返回后即为可交付」式期货宣称恒剥离。
//     取证 msg[5]：B.1 禁了还原/复刻/100%，但「完整淘宝首页原型…可交付」漏拦。
//   - goal layer (SPEC-453 M3-3, opts.goal): worker ralph-loop goalCheck 判定 met=false →
//     完成态/期货宣称整句剥离，如实追加「目标未完全达成：对象覆盖 N%，未达项：…」——
//     禁完成态断言（SPEC-427 D 精神）；met=true 或无 goal → 零干预（旧签名字节一致）。
//   - draft without any claim → returned byte-identical.
// The process log (ChatProcessStep[]) itself is never touched here.
//
// Pure function, no external state. A null/undefined canvas (project or page deleted before the
// done dispatch) is treated as "cannot disprove the claim" → placeholder layer passthrough.

import type { CanvasState, ChatProcessStep, GoalSummary } from '../types';
import type { CritiqueIssue } from '../api';

/** Placeholder = an image object whose generation has not landed (no src) or failed (error set). */
export function hasPlaceholders(canvas: CanvasState | null | undefined): boolean {
  if (!canvas) return true; // unknown final state → conservative: keep the claim
  return canvas.objects.some(o => o.type === 'image' && (!o.src || !!o.error));
}

/** A placeholder-claim sentence mentions 占位 AND a generation verb. Deliberately strict on the
 * co-occurrence: sentences like「已保留占位图」(no generation claim) survive. */
const PLACEHOLDER_RE = /占位/;
const GEN_VERB_RE = /(生成|渲染|替换|产出|落地|排队|启动|制作)/;

/** Self-check claim markers (修复轮 A.1). Narrow on purpose:「按审查意见修正」/「按反馈修正」
 * carry none of these and are never stripped. Covers 自检完成/已自检/自检修正完成/
 * 已按视觉规范(完成)/视觉检查通过 and close variants. */
const SELF_CLAIM_RE = /(自检|视觉检查通过|视觉审查通过|已按视觉规范|按视觉规范完成|符合视觉规范)/;

/** Replica-claim markers (修复轮 B.1). Absolute-fidelity words are claims by themselves; the
 * fidelity verbs need a completion-claim verb to co-occur (narrowing keeps asking/progressive/
 * declaration sentences alive). */
const REPLICA_ABS_RE = /(100%|100％|百分之百|一模一样)/;
const REPLICA_VERB_RE = /(还原|复刻|仿制)/;
const CLAIM_DONE_RE = /(已|完成|达成|做到|收工|搞定|通过)/;

/** Phases that evidence objective reference grounding (the SPEC-402 extract/diff pipeline ran). */
const GROUNDING_PHASES: string[] = ['extract', 'diff'];

/** SPEC-427 D — 完整性宣称标记：完整搭建/完整原型/可交付/整稿完成类。noReference 路径下
 * verdict.pass≠true（含无 verdict）时整句剥离；句内边界限定，不跨句误伤。 */
const COMPLETENESS_RE = /(完整[^。；;！!\n]{0,8}(原型|搭建|完成|落地)|可交付|整稿完成|交付标准|达到交付)/;
/** SPEC-427 D — 期货式完整性宣称（「待返回后即为可交付」）：未来永无当下证据，
 * noReference 路径恒剥离（即使 verdict 已 pass — 图像在途时的交付断言仍无据）。 */
const FUTURE_CLAIM_RE = /(即为可交付|待[^。；;！!\n]{0,10}(返回|落地|生成)[^。；;！!\n]{0,10}(即为|即是|即可|就是)|(返回|落地|生成)后(?:即为|即可)交付)/;

/** Sentence split keeping terminators attached (。；;！! and newline are boundaries). */
function splitSentences(text: string): string[] {
  return text.match(/[^。；;！!\n]+[。；;！!\n]*/g) ?? [text];
}

/** Drop whole sentences matching pred; byte-identical passthrough when nothing matches. */
function stripSentences(text: string, pred: (s: string) => boolean): string {
  const parts = splitSentences(text);
  const kept = parts.filter(s => !pred(s));
  if (kept.length === parts.length) return text;
  return kept.join('').trim();
}

/** True when the process log evidences a real visual review — a critique phase actually ran. */
export function hasCritiqueEvidence(process?: ChatProcessStep[] | null): boolean {
  return Array.isArray(process) && process.some(s => s && s.phase === 'critique');
}

// ===== SPEC-427 B — 宣称对账 =====
// 取证：12/20 轮无 process 字段（无审查证据）仍宣称「已完成修复」；verdict 冻结 hard=2
// 连续 8 轮而每轮都宣称修正完成。对账规则：「已修复/已完成修正」类宣称必须与本轮证据
//（post-fix critique verdict 指纹变化 + ops diff）对得上；两者都无变化 → 宣称改写为
//「修正未生效（审查结果未变，问题仍在：<清单>）」。与 A.1/B.1 既有剥离规则叠加不冲突：
// 先跑既有剥离（buildDoneText），再跑对账改写（reconcileFixClaims，由 store 的
// RECONCILE_CLAIM 在下一轮 critique 对账命中时触发）。

/** Fix-completion claim markers（窄口径：只命中「修复/修正/调整已完成」类宣称；
 * 进行时叙述（「正在修正」）与问题描述不命中）。 */
const FIX_CLAIM_RE = /(已修复|已修正|已调整|已修改|已完成修复|已完成修正|已完成调整|已经修复|已经修正|修复完成|修正完成|调整完成|修改完成|修复完毕|修正完毕|逐项修复|逐项修正|精准修复|精准修正|问题已解决|已解决上述问题|已按[^。；;！!\n]{0,12}(修复|修正|调整))/;

/** True when the text carries at least one fix-completion claim sentence. */
export function hasFixClaim(text: string): boolean {
  if (typeof text !== 'string' || text.length === 0) return false;
  return splitSentences(text).some(s => FIX_CLAIM_RE.test(s));
}

/** 对账证据（由调用方 — App 审查循环 — 在下一轮 critique 后核对提供）。 */
export interface ReconcileEvidence {
  /** post-fix critique 的 hard 指纹集合相对上一轮发生变化（= 修正生效的客观信号） */
  verdictChanged: boolean;
  /** 本轮 tool ops diff 为空（无任何画布操作/图像任务落地） */
  opsEmpty: boolean;
  /** 仍存在的 hard 问题清单（改写句尾部列出） */
  remaining: CritiqueIssue[];
}

/**
 * Reconcile fix-completion claims against the round's evidence. Both verdict fingerprint AND
 * ops diff unchanged → every claim sentence is rewritten: the FIRST becomes
 *「修正未生效（审查结果未变，问题仍在：<清单>）」, subsequent claim sentences drop (同一事实
 * 不重复)。Non-claim sentences stay verbatim. Evidence supports the claim (verdict changed OR
 * ops landed) OR no claim present → byte-identical passthrough.
 */
export function reconcileFixClaims(text: string, ev: ReconcileEvidence): string {
  if (typeof text !== 'string' || text.length === 0) return text;
  if (ev.verdictChanged || !ev.opsEmpty) return text;
  const parts = splitSentences(text);
  if (!parts.some(s => FIX_CLAIM_RE.test(s))) return text;
  const list = (ev.remaining ?? []).map(i => `${i.desc}${i.region ? `（位置：${i.region}）` : ''}`).join('；');
  const note = `修正未生效（审查结果未变，问题仍在：${list}）。`;
  let placed = false;
  const kept: string[] = [];
  for (const s of parts) {
    if (FIX_CLAIM_RE.test(s)) {
      if (!placed) { kept.push(note); placed = true; }
      // later claim sentences drop — same disproven fact, never repeated
    } else {
      kept.push(s);
    }
  }
  return kept.join('').trim();
}

/** True when the process log evidences reference grounding — an extract/diff phase actually ran. */
export function hasGroundingEvidence(process?: ChatProcessStep[] | null): boolean {
  return Array.isArray(process) && process.some(s => s && GROUNDING_PHASES.includes(s.phase));
}

/** SPEC-427 D: True when a critique step recorded verdict.pass === true — 完整性宣称的唯一证据。
 * 旧响应无 verdict 摘要 → false（保守：无据即剥）。 */
export function hasPassingCritique(process?: ChatProcessStep[] | null): boolean {
  return Array.isArray(process) && process.some(s => s && s.phase === 'critique' && s.verdict?.pass === true);
}

/** A sentence claims replication fidelity (see REPLICA_* header). */
function isReplicaClaim(s: string): boolean {
  return REPLICA_ABS_RE.test(s) || (REPLICA_VERB_RE.test(s) && CLAIM_DONE_RE.test(s));
}

// ===== SPEC-427 A — 同文案复读抑制 =====
// 取证：14:32-14:45 十条连续 assistant 消息，同一「两项精准修正」宣称重复 7 次。归一化后
// 包含关系即视为复读 → store 的 AGENT_RESULT 落泡点合并为一条，不重复推送。

/** Normalize for claim comparison: lowercase, strip whitespace/punctuation/symbols. */
export function normalizeClaimText(s: string): string {
  return (typeof s === 'string' ? s : '').toLowerCase().replace(/[\s\p{P}\p{S}]+/gu, '');
}

/** True when `next` is a repeat of `prev` (either normalized form contains the other). An empty
 * normalized `next` counts as repeat (nothing new to push). Only ever called on CONSECUTIVE
 * assistant landings — a user bubble in between breaks the chain by construction. */
export function isRepeatClaim(prev: string, next: string): boolean {
  const a = normalizeClaimText(prev);
  const b = normalizeClaimText(next);
  if (!b) return true;
  if (!a) return false;
  return a.includes(b) || b.includes(a);
}

/**
 * Align a done-message draft with the final canvas + process evidence (see header). Called at
 * EVERY worker-reply landing point (spec 提示 1): the two CHAT_APPEND collection points in
 * App.tsx (review loop / diff loop) and the store's AGENT_RESULT reducer.
 */
export function buildDoneText(
  finalCanvas: CanvasState | null | undefined,
  draftText: string,
  process?: ChatProcessStep[] | null,
  /** SPEC-427 D: opts.noReference = insistsNoReference 路径（复刻意图+无附图+坚持无参考，
   * 判定见 replicate.ts isNoRefReplicaTurn）— 该路径完整性宣称需 verdict.pass=true 证据才保留，
   * 期货宣称恒剥离。SPEC-453 M3-3: opts.goal = worker goalCheck 判定（met=false → 诚实收口层）。
   * 省略 opts 与 D 之前行为逐字节一致（旧签名兼容）。 */
  opts?: { noReference?: boolean; goal?: GoalSummary }
): string {
  if (typeof draftText !== 'string' || draftText.length === 0) return draftText;
  let out = draftText;
  // Self-claim layer: no critique evidence (or no process at all) → strip 自检-style claims whole.
  if (!hasCritiqueEvidence(process)) out = stripSentences(out, s => SELF_CLAIM_RE.test(s));
  // Replica-claim layer: no grounding evidence → strip 还原/复刻/100% claim sentences whole.
  if (!hasGroundingEvidence(process)) out = stripSentences(out, isReplicaClaim);
  // Placeholder layer: claims survive only while placeholders actually remain.
  if (!hasPlaceholders(finalCanvas)) out = stripSentences(out, s => PLACEHOLDER_RE.test(s) && GEN_VERB_RE.test(s));
  // SPEC-427 D: 无参考复刻路径 — 先跑既有剥离（上三层）再叠完整性层：期货宣称恒剥；
  // 完整性宣称无 passing critique 证据即剥。保真声明「无参考图，按通用规范设计」不含
  // 完整性/期货标记词，永远存活。
  if (opts?.noReference) {
    out = stripSentences(out, s => FUTURE_CLAIM_RE.test(s));
    if (!hasPassingCritique(process)) out = stripSentences(out, s => COMPLETENESS_RE.test(s));
  }
  // SPEC-453 M3-3 — 诚实收口层：worker goal loop 触顶/熔断（met=false）时完成态/期货宣称
  // 整句剥离（禁完成态断言，SPEC-427 D 精神），并如实追加未达项摘要 — 缺什么说什么。
  // met=true / 无 goal → 本层零干预。
  if (opts?.goal && opts.goal.met === false) {
    out = stripSentences(out, s => FUTURE_CLAIM_RE.test(s) || COMPLETENESS_RE.test(s));
    const list = (opts.goal.missing ?? []).slice(0, 6).join('；');
    const pct = Math.round((opts.goal.coverage ?? 0) * 100);
    const note = `（目标未完全达成：对象覆盖 ${pct}%${list ? `，未达项：${list}` : ''}）`;
    out = out.trim() ? `${out.trim()}\n\n${note}` : note;
  }
  return out;
}
